import { saveToModule } from '@/lib/api';

export const IGROW_AREA_NAMES = [
  'Daily Living Skills',
  'Social Skills & Emotional Regulation',
  'Functional Academics',
  'Creative Arts',
  'Pre-Vocational Skills & Entrepreneurship',
] as const;

export const IGROW_COMMUNICATION_OPTIONS = ['WhatsApp', 'Phone Call', 'Email'] as const;

export type IgrowAreaName = (typeof IGROW_AREA_NAMES)[number];
export type IgrowCommunicationPreference = (typeof IGROW_COMMUNICATION_OPTIONS)[number] | '';

/** AppClust module slug. Create a module with this exact name. */
export const IGROW_APPCLUST_MODULE = 'igrow-programme';

/**
 * Flat columns to create on the AppClust module.
 * Every value is text so each column can be a Text or Long text field.
 */
export type IgrowAppClustFields = {
  parentName: string;
  parentPhone: string;
  parentEmail: string;
  childName: string;
  childAge: string;
  childSchool: string;
  communicationPreference: string;
  childLoves: string;
  childActivities: string;
  childGoals: string;
  areasOfInterest: string;
  consentContact: string;
  consentAccuracy: string;
  submittedAt: string;
  source: string;
};

export type IgrowRegistrationPayload = {
  parent: {
    name: string;
    phone: string;
    email: string;
  };
  child: {
    name: string;
    age: number;
    school: string;
    communicationPreference: IgrowCommunicationPreference;
  };
  interests: {
    loves: string;
    activities: string;
    goals: string;
  };
  areas: IgrowAreaName[];
  consent: {
    contact: boolean;
    accuracy: boolean;
  };
  submittedAt: string;
};

export class IgrowRegistrationError extends Error {
  status: number;

  constructor(message: string, status = 400) {
    super(message);
    this.name = 'IgrowRegistrationError';
    this.status = status;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new IgrowRegistrationError(`${label} is required.`);
  }
  return value.trim();
}

function optionalString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function toAppClustFields(payload: IgrowRegistrationPayload): IgrowAppClustFields {
  return {
    parentName: payload.parent.name,
    parentPhone: payload.parent.phone,
    parentEmail: payload.parent.email,
    childName: payload.child.name,
    childAge: String(payload.child.age),
    childSchool: payload.child.school,
    communicationPreference: payload.child.communicationPreference,
    childLoves: payload.interests.loves,
    childActivities: payload.interests.activities,
    childGoals: payload.interests.goals,
    areasOfInterest: payload.areas.join(', '),
    consentContact: payload.consent.contact ? 'Yes' : 'No',
    consentAccuracy: payload.consent.accuracy ? 'Yes' : 'No',
    submittedAt: payload.submittedAt,
    source: 'igrow-programme',
  };
}

/** Save through the shared lib helper, which posts to /api/v1/modules/{module}/saveToDB. */
export async function saveIgrowToAppClust(payload: IgrowRegistrationPayload) {
  return saveToModule(IGROW_APPCLUST_MODULE, toAppClustFields(payload));
}

/**
 * Validate a registration and save it to the AppClust module.
 */
export async function saveIgrowRegistration(input: unknown): Promise<{ received: true }> {
  if (!isRecord(input) || !isRecord(input.parent) || !isRecord(input.child) || !isRecord(input.interests) || !isRecord(input.consent)) {
    throw new IgrowRegistrationError('Registration payload is incomplete.');
  }

  const communication = optionalString(input.child.communicationPreference);
  if (communication && !IGROW_COMMUNICATION_OPTIONS.includes(communication as (typeof IGROW_COMMUNICATION_OPTIONS)[number])) {
    throw new IgrowRegistrationError('Communication preference is not recognised.');
  }

  const age = input.child.age;
  if (typeof age !== 'number' || !Number.isInteger(age) || age < 1 || age > 25) {
    throw new IgrowRegistrationError('Child age must be a whole number from 1 to 25.');
  }

  if (input.consent.contact !== true || input.consent.accuracy !== true) {
    throw new IgrowRegistrationError('Both consent confirmations are required.');
  }

  const rawAreas = Array.isArray(input.areas) ? input.areas : [];
  const areas = IGROW_AREA_NAMES.filter((name) => rawAreas.includes(name));

  const payload: IgrowRegistrationPayload = {
    parent: {
      name: requiredString(input.parent.name, 'Parent name'),
      phone: requiredString(input.parent.phone, 'Contact number'),
      email: requiredString(input.parent.email, 'Email address'),
    },
    child: {
      name: requiredString(input.child.name, 'Child name'),
      age,
      school: optionalString(input.child.school),
      communicationPreference: communication as IgrowCommunicationPreference,
    },
    interests: {
      loves: requiredString(input.interests.loves, 'What your child loves'),
      activities: optionalString(input.interests.activities),
      goals: optionalString(input.interests.goals),
    },
    areas,
    consent: {
      contact: true,
      accuracy: true,
    },
    submittedAt: typeof input.submittedAt === 'string' && input.submittedAt
      ? input.submittedAt
      : new Date().toISOString(),
  };

  const apiKey = process.env.APPCLUST_API_KEY;
  const baseUrl = process.env.APPCLUST_BASE_URL;

  if (!apiKey || !baseUrl) {
    throw new IgrowRegistrationError('AppClust is not configured.', 500);
  }

  const response = await fetch(`${baseUrl}/v1/modules/${IGROW_APPCLUST_MODULE}/saveToDB`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(toAppClustFields(payload)),
  });

  if (!response.ok) {
    throw new IgrowRegistrationError('Unable to save this registration right now.', 502);
  }

  return { received: true };
}

export function getIgrowRegistrationEndpoint(): string {
  const configured = process.env.NEXT_PUBLIC_IGROW_REGISTRATION_ENDPOINT?.trim();
  return configured || '/api/registrations/igrow-programme';
}
